import { Button, Table } from 'antd';
import { MapPin, Plus } from 'lucide-react';
import { useNavigate } from 'react-router';
import { EmptyState } from '../../components/EmptyState';
import { TableSkeleton } from '../../components/TableSkeleton';
import { buildZoneColumns } from './build-zone-columns';
import type { Zone } from './reference-data.api';

interface ZonesPanelProps {
  zones: Zone[];
  loading: boolean;
  onDeactivate: (row: Zone) => Promise<void>;
  onReactivate: (row: Zone) => Promise<void>;
}

/**
 * Client-side paging (Ant's default) — the small-fixed-catalogue exception to server-side lists (CLAUDE.md → Lists and tables).
 * New and Edit open the zone's own page (ZoneEditorPage), where the map has room to draw on.
 */
export function ZonesPanel({ zones, loading, onDeactivate, onReactivate }: ZonesPanelProps) {
  const navigate = useNavigate();
  const columns = buildZoneColumns({ onEdit: (row) => navigate(`/reference-data/zones/${row.id}`), onDeactivate, onReactivate });
  const newButton = (
    <Button type="primary" icon={<Plus className="size-4" />} onClick={() => navigate('/reference-data/zones/new')}>
      New zone
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">{newButton}</div>
      {loading && <TableSkeleton label="Loading zones" />}
      {!loading && zones.length === 0 && (
        <EmptyState
          icon={MapPin}
          title="No zones yet"
          description="Rate lines key on a zone or named point, never a free-text address — add the ones this deployment uses."
        >
          {newButton}
        </EmptyState>
      )}
      {!loading && zones.length > 0 && <Table rowKey="id" dataSource={zones} columns={columns} pagination={{ pageSize: 25 }} scroll={{ x: 'max-content' }} />}
    </div>
  );
}
